import React, { useState, useEffect } from 'react';
import Menu from './Menu';
import Header from './Header';
import DockBar from './DockBar';
import MouseGlow from '../ui/MouseGlow';
import { PullToRefresh, DebugPanelTabs } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { usePreferences } from '../../context/PreferencesContext';
import { isAdmin as checkIsAdmin } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';

const SIDEBAR_ENABLED_KEY = 'cabaza_sidebar_enabled';

/**
 * User layout with vertical navigation sidebar and content area
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.currentPage - Current active page
 * @param {Function} props.onNavigate - Navigation handler
 * @param {Function} props.onLogout - Logout handler
 */
const UserLayout = ({ children, currentPage, onNavigate, onLogout }) => {
    // Get theme colors for MouseGlow
    const { currentTheme } = useTheme();

    // Check if user is admin
    const isAdmin = checkIsAdmin();
    const { dockVisible, dockSize, dockPosition } = usePreferences();
    const { t } = useTranslation();

    // Full-screen pages (no padding, no header)
    const isFullScreenPage = currentPage === 'map';

    // Home page smartphone is completely fullscreen (no sidebar, no header)
    const isHomePage = currentPage === 'home';

    // Sidebar enabled state - enabled if items are pinned or if explicitly enabled
    const [isSidebarEnabled, setIsSidebarEnabled] = useState(() => {
        const stored = localStorage.getItem(SIDEBAR_ENABLED_KEY);
        if (stored !== null) return stored === 'true';

        // Default: enable sidebar if there are pinned items
        try {
            const pinnedItems = JSON.parse(localStorage.getItem('cabazaPinnedItems') || '[]');
            return pinnedItems.length > 0;
        } catch (e) {
            return false;
        }
    });

    // Listen for pinned items changes to auto-enable sidebar
    useEffect(() => {
        const handlePinnedItemsChange = () => {
            try {
                const pinnedItems = JSON.parse(localStorage.getItem('cabazaPinnedItems') || '[]');
                if (pinnedItems.length > 0 && !isSidebarEnabled) {
                    setIsSidebarEnabled(true);
                    localStorage.setItem(SIDEBAR_ENABLED_KEY, 'true');
                }
            } catch (e) {
                console.error('Error auto-enabling sidebar:', e);
            }
        };

        window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange);
        return () => window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange);
    }, [isSidebarEnabled]);

    // Mobile sidebar state - closed by default on mobile
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Toggle mobile menu or navigate to home if sidebar disabled
    const toggleMobileMenu = () => {
        if (!isSidebarEnabled) {
            // If sidebar is disabled, navigate to home
            onNavigate('home');
            return;
        }
        // Only toggle on mobile (sm breakpoint = 640px)
        if (window.innerWidth < 640) {
            setIsMobileMenuOpen(!isMobileMenuOpen);
        }
    };

    // Toggle sidebar enabled state
    const toggleSidebarEnabled = (enabled) => {
        setIsSidebarEnabled(enabled);
        localStorage.setItem(SIDEBAR_ENABLED_KEY, enabled.toString());
    };

    // Close mobile menu when navigating
    const handleNavigate = (page) => {
        setIsMobileMenuOpen(false);
        onNavigate(page);
    };

    // Close menu when resizing to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 640) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Home page smartphone takes full screen (no PullToRefresh - uses fixed positioning)
    if (isHomePage) {
        return (
            <div className="h-screen w-screen">
                {children}
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-white">
            {/* Mouse glow effect with theme colors */}
            <MouseGlow
                primaryColor={currentTheme?.primaryColor}
                secondaryColor={currentTheme?.secondaryColor}
            />

            {/* Mobile overlay - only if sidebar is enabled */}
            {isSidebarEnabled && isMobileMenuOpen && (
                <div
                    className="sm:hidden fixed inset-0 bg-black/50 z-[9998]"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Vertical Sidebar - Hidden on mobile, overlay when open - only if enabled */}
            {isSidebarEnabled && (
                <div className={`
                    fixed sm:relative z-[9999]
                    transform transition-transform duration-300 ease-in-out
                    ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
                `}>
                    <Menu
                        currentPage={currentPage}
                        onNavigate={handleNavigate}
                        onLogout={onLogout}
                        isSidebarEnabled={isSidebarEnabled}
                        onToggleSidebar={toggleSidebarEnabled}
                    />
                </div>
            )}

            {/* Main Content Area */}
            {isFullScreenPage ? (
                <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Header for fullscreen pages - only on mobile when sidebar is enabled */}
                    {isSidebarEnabled && (
                        <div className="sm:hidden flex-shrink-0 bg-white border-b border-gray-200 px-3 py-2">
                            <Header
                                onToggleMenu={toggleMobileMenu}
                                onLogout={onLogout}
                                onNavigate={onNavigate}
                                showMenuButton={!isMobileMenuOpen}
                                isSidebarEnabled={isSidebarEnabled}
                            />
                        </div>
                    )}
                    {/* Fullscreen content - pass sidebar state for floating controls */}
                    <div className="flex-1 overflow-hidden">
                        {React.cloneElement(children, {
                            isSidebarEnabled,
                            onNavigateHome: () => onNavigate('home'),
                            onNavigateAddContact: () => onNavigate('contacts')
                        })}
                    </div>
                </div>
            ) : (
                <div className={`flex-1 overflow-auto bg-white ${!isSidebarEnabled ? 'sm:ml-0' : ''}`}>
                    <PullToRefresh>
                        <div className="max-w-7xl mx-auto px-4 pt-2 pb-4 sm:px-6 sm:pt-3 sm:pb-6">
                            {/* Header with hamburger on mobile */}
                            <Header
                                onToggleMenu={toggleMobileMenu}
                                onLogout={onLogout}
                                onNavigate={onNavigate}
                                showMenuButton={!isMobileMenuOpen || !isSidebarEnabled}
                                isSidebarEnabled={isSidebarEnabled}
                            />

                            {/* Page Content */}
                            {children}

                            {/* Footer */}
                            <div className="text-center py-6 mt-4 border-t border-gray-100">
                                <p className="text-xs text-gray-400">
                                    &copy; Cabaza {new Date().getFullYear()} — {t.errorBoundary?.allRightsReserved || 'All rights reserved'}
                                </p>
                            </div>
                        </div>
                    </PullToRefresh>
                </div>
            )}

            {/* Floating Dock Bar */}
            {dockVisible !== false && (
                <DockBar currentPage={currentPage} onNavigate={onNavigate} size={dockSize} dockPosition={dockPosition} isSidebarEnabled={isSidebarEnabled} />
            )}

            {/* Debug Panel (for admins only - handles its own visibility) */}
            {isAdmin && <DebugPanelTabs />}
        </div>
    );
};

export default UserLayout;
